<script lang="ts" setup>
import AuthorityBanner from '../AuthorityBanner/AuthorityBanner.vue';
import DiagnoseLink from '../DiagnoseLink.vue';
import EveryonesReviews from '../EveryonesReviews/EveryonesReviews.vue';
import FAQ, { type QAndA } from '../FAQ/FAQ.vue';
import HeroPrepareTitle from '../HeroPrepareTitle.vue';
import LpBreadcrumbs, { type Breadcrumbs } from '../LpBreadcrumbs.vue';
import MediaSection from '../MediaSection.vue';
import ProblemPresentation from '../ProblemPresentation/ProblemPresentation.vue';
import ProductLpHero from '../ProductLpHero.vue';
import ProductLpTemplateConsultProfessional from '../ProductLpTemplateConsultProfessional.vue';
import ResearchData from '../ResearchData/ResearchData.vue';
import ScrollToTopButton from '../ScrollToTopButton/ScrollToTopButton.vue';
import SeminarSection from '../SeminarSection.vue';
import Steps from '../Steps/Steps.vue';
import WhereToConsult from '../WhereToConsult/WhereToConsult.vue';
import WhyFreeConsultation from '../WhyFreeConsultation/WhyFreeConsultation.vue';
import YouCanDoThis from '../YouCanDoThis/YouCanDoThis.vue';
import { commonFaqList } from '../common/commonFaqList';
import type { KvImageSrcset } from '../kvImageSrcsets';
import okanekoMagazineBanner from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/okaneko-magazine-banner.png/okaneko-magazine-banner.webp';
import {
  SeminarFragment,
  useProductLpDevKeywordsQuery,
} from '../useProductLpDevKeywordsQuery';
import ProblemPresentationCard1 from './ProblemPresentationCard1.vue';
import ProblemPresentationCard2 from './ProblemPresentationCard2.vue';
import ProblemPresentationCard3 from './ProblemPresentationCard3.vue';
import ProblemPresentationHeading from './ProblemPresentationHeading.vue';
import ResearchDataCard1 from './ResearchDataCard/ResearchDataCard1.vue';
import ResearchDataCard2 from './ResearchDataCard/ResearchDataCard2.vue';
import ResearchDataCard3 from './ResearchDataCard/ResearchDataCard3.vue';
import ResearchDataCard4 from './ResearchDataCard/ResearchDataCard4.vue';
import ResearchDataCard5 from './ResearchDataCard/ResearchDataCard5.vue';
import ResearchDataCard6 from './ResearchDataCard/ResearchDataCard6.vue';
import ResearchDataCard7 from './ResearchDataCard/ResearchDataCard7.vue';
import WhereToConsultCard1 from './WhereToConsultCard/WhereToConsultCard1.vue';
import WhereToConsultCard2 from './WhereToConsultCard/WhereToConsultCard2.vue';
import WhereToConsultCard3 from './WhereToConsultCard/WhereToConsultCard3.vue';
import WhereToConsultCard4 from './WhereToConsultCard/WhereToConsultCard4.vue';
import WhereToConsultCard5 from './WhereToConsultCard/WhereToConsultCard5.vue';
import YouCanDoThisCard1 from './YouCanDoThisCard1.vue';
import YouCanDoThisCard2 from './YouCanDoThisCard2.vue';
import YouCanDoThisCard3 from './YouCanDoThisCard3.vue';
import kvImage380 from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/educationFund/kv_educationFund.png/kv_educationFund_width=380.webp';
import kvImage560 from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/educationFund/kv_educationFund.png/kv_educationFund_width=560.webp';
import kvImage600 from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/educationFund/kv_educationFund.png/kv_educationFund_width=600.webp';
import kvImage1200 from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/educationFund/kv_educationFund.png/kv_educationFund_width=1200.webp';
import AppFooter from '@/components/organisms/AppFooter/AppFooter.vue';
import PointsSummary from '@/components/organisms/PointsSummary/PointsSummary.vue';
import InterviewCard from '~/components/organisms/InterviewCard/InterviewCard.vue';

const keyword = '教育資金';

const { data } = await useProductLpDevKeywordsQuery({
  keywords: ['教育'], // 拾いたいキーワード: '教育資金', '教育'
});
const seminars = useComputedFragment(
  SeminarFragment,
  () => data.value?.filterSeminars.edges.map((elm) => elm.node) ?? []
);

const title =
  '子どもの教育資金の準備方法・貯め方｜教育資金の貯め方をFPに相談するならオカネコ';
const description =
  '子どもの教育資金、いつまでに、いくら貯めるべき？ 各ご家庭のライフイベントに合わせた教育資金の貯め方を、プロのFPがご提案します。';

const dateModified = '2025-01-21';

const breadcrumbs: Breadcrumbs = [
  {
    label: 'オカネコ',
    location: '/',
    description: useConstants().WEB.description,
  },
  {
    label: `${keyword}のやり方・相談`,
    description: description,
  },
];

const kvImageSrcset: KvImageSrcset = {
  1200: kvImage1200,
  600: kvImage600,
  560: kvImage560,
  380: kvImage380,
};

const faqList: QAndA[] = [
  {
    q: 'ライフプランシミュレーションをすると何がわかるのですか？',
    a: '支出が増えるタイミングや貯金しやすいタイミングが可視化されるため、いつまでにいくら教育資金を貯めるべきかが分かります。さらに、住宅購入資金や老後に必要な資金も把握でき、理想の生活を実現するための計画的な資金準備が可能になります。',
  },
  {
    q: '学資保険の加入を検討していますがどのようなリスクがありますか？',
    a: '学資保険は保険料が高めに設定されているため、途中で解約すると損をする可能性が高いです。また、近年では解約返戻率も下降傾向にあるため、学資保険の代わりにNISAや低解約返戻金型終身保険で備える例もあります。ライフプランシミュレーションを行い、支払いが続けられる適正な保険料を確認することをおすすめします。',
  },
  ...commonFaqList,
];

const mediaProps = {
  location: 'https://okane-kenko.jp/media/',
  imageSrc: okanekoMagazineBanner,
  relatedArticleLinks: [
    {
      title:
        '大学の学費はいくらかかる？4年間の平均費用や活用できる制度を解説します',
      location: 'https://okane-kenko.jp/media/private-university-tuition/',
    },
    {
      title:
        '教育ローンの返済は大変？ 借入額の平均から返済期間別にシミュレーション',
      location: 'https://okane-kenko.jp/media/education-loan-repayment/',
    },
    {
      title:
        '学資保険は要らない？ 必要・不要な人の特徴や代わりとなる教育資金の準備方法',
      location: 'https://okane-kenko.jp/media/educational-insurance/',
    },
  ],
};

const interviewIds = ['1', '2', '3', '4'] as const;

useAppMeta({
  title: title,
  hasTitleSuffix: false,
  description: description,
});

const {
  public: { appOrigin },
} = useRuntimeConfig();
const route = useRoute();
const currentCanonicalUrl = appOrigin + route.path.replace(/\/+$/, '');
useJsonldWebPageForProductLP(
  currentCanonicalUrl,
  title,
  description,
  dateModified
);
</script>

<template>
  <div :class="$style.layout">
    <LpBreadcrumbs :breadcrumbs="breadcrumbs" />
    <ProductLpHero :class="$style.hero" :kv-image-srcset="kvImageSrcset">
      <template #heading> {{ keyword }}についてお悩みの方へ</template>
      <template #title>
        <HeroPrepareTitle :keyword="keyword" />
      </template>
      <template #description>
        {{ description }}
      </template>
    </ProductLpHero>
    <AuthorityBanner :class="$style.authority" />
    <ProblemPresentation :class="$style.problemPresentation" :keyword="keyword">
      <template #heading>
        <ProblemPresentationHeading />
      </template>
      <template #default>
        <ProblemPresentationCard1 :image-number="1" />
        <ProblemPresentationCard2 :image-number="2" />
        <ProblemPresentationCard3 :image-number="3" />
      </template>
    </ProblemPresentation>
    <WhereToConsult
      :class="$style.whereToConsult"
      :keyword="keyword"
      title="教育資金はいくら必要？自分にあった貯め方は？"
      :date-modified="dateModified"
    >
      <WhereToConsultCard1 :image-number="0" />
      <WhereToConsultCard2 :image-number="1" />
      <WhereToConsultCard3 :image-number="2" />
      <WhereToConsultCard4 :image-number="3" />
      <WhereToConsultCard5 :image-number="4" />
    </WhereToConsult>
    <ResearchData :keyword="keyword" :class="$style.researchData">
      <ResearchDataCard1 :num="1" />
      <ResearchDataCard2 :num="2" />
      <ResearchDataCard3 :num="3" />
      <ResearchDataCard4 :num="4" />
      <ResearchDataCard5 :num="5" />
      <ResearchDataCard6 :num="6" />
      <ResearchDataCard7 :num="7" />
    </ResearchData>
    <ProductLpTemplateConsultProfessional
      :keyword="keyword"
      :class="$style.consultProfessional"
    />
    <YouCanDoThis :keyword="keyword" :class="$style.youCanDoThis">
      <YouCanDoThisCard1 :image-number="1" />
      <YouCanDoThisCard2 :image-number="2" />
      <YouCanDoThisCard3 :image-number="3" />
    </YouCanDoThis>
    <WhyFreeConsultation :class="$style.whyFreeConsultation" />
    <PointsSummary :class="$style.pointsSummary" />
    <DiagnoseLink :class="$style.diagnoseLink1" />
    <Steps :class="$style.steps" />
    <EveryonesReviews :class="$style.everyonesReviews">
      <InterviewCard
        v-for="id in interviewIds"
        :id="id"
        :key="id"
        variant="complicated"
      />
    </EveryonesReviews>
    <FAQ :class="$style.faq" :faq-list="faqList" />
    <DiagnoseLink :class="$style.diagnoseLink2" />
    <section
      v-if="seminars.length > 0 || !!mediaProps"
      :class="$style.seminarAndMedia"
    >
      <SeminarSection
        :seminars="seminars"
        seminars-link="/seminar?categories=ASSET_MANAGEMENT"
        :category="keyword"
      />
      <MediaSection :keyword="keyword" :media="mediaProps" />
      <DiagnoseLink :class="$style.diagnoseLink3" />
    </section>
    <AppFooter />
    <ScrollToTopButton :class="$style.layout__scrollToTop"></ScrollToTopButton>
  </div>
</template>

<style lang="scss" module>
@use '../_constants.scss' as *;

img {
  vertical-align: top;
}

.layout {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  @include mobile-only {
    padding-bottom: 200px;
  }
  &__scrollToTop {
    display: block;
    position: absolute;
    right: 40px;
    bottom: 40px;
    @include mobile-only {
      right: 0;
      left: 0;
      margin: auto auto;
      bottom: 64px;
    }
  }
}

.hero {
  align-self: stretch;
}

.authority {
  max-width: 520px;
  width: 100vw;
}

.problemPresentation {
  margin-top: 40px;
}

.whereToConsult {
  margin-top: 64px;
  width: 100%;

  @include mobile-only {
    margin-top: 40px;
  }
}

.researchData {
  margin-top: 64px;
  width: 100%;

  @include mobile-only {
    margin-top: 40px;
  }
}

.consultProfessional {
  margin-top: 80px;

  @include mobile-only {
    margin-top: 20px;
  }
}

.youCanDoThis {
  margin-top: 40px;

  @include mobile-only {
    margin-top: 20px;
  }
}

.whyFreeConsultation {
  margin-top: 40px;

  @include mobile-only {
    margin-top: 20px;
  }
}

.pointsSummary {
  max-width: $content-width;
  padding: 16px;

  @include tablet-upper {
    padding-block: 0;
  }

  &:nth-child(n + 2) {
    margin-top: 20px;

    @include tablet-upper {
      margin-top: 40px;
    }
  }
}
.seminarAndMedia {
  width: 100%;
  margin-top: 64px;
  padding: 56px 0;
  background-color: $color-surface-accent-primary-light;
  @include mobile-only {
    padding: 32px 0;
  }
  & > * {
    margin-right: auto;
    margin-left: auto;

    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.steps {
  margin-top: 64px;

  @include mobile-only {
    margin-top: 48px;
  }
}

.faq {
  width: 100%;
}

.diagnoseLink1 {
  margin-top: 40px;
}
.diagnoseLink2 {
  margin-top: 40px;
}
.diagnoseLink3 {
  margin-top: 56px;
}
</style>
